import API from '@api/API';
import { ENDPOINTS } from '@api/endpoints';
import type { GetSuperadminCompanyResponse } from '@api/types/company';
import { useQuery } from '@tanstack/react-query';
import { adminKeys } from './query-keys';

/**
 * The platform-admin read of one organisation, `GET /superadmin/company/:type/:id`, shared by the
 * admin company page (`features/platform-admin`) and the browser tab title (`features/page-title`).
 * Both observe the same key, so the title costs no request of its own and names the record the
 * page shows.
 *
 * `:id` IS NOT A COMPANY ID FOR EVERY TYPE. The server reads `prisma.company` for `client` and
 * `prisma.team` for `fiduciary` and `audit_firm`, so team 67 and client company 67 are different
 * organisations. Resolving an admin route's `:id` through `GET /company/:id` titled a fiduciary
 * page with an unrelated client company (#732).
 *
 * The answer is the record itself plus the route's `type`, with no `{ company }` envelope.
 *
 * ⚠️ AN EARLY EXCEPTION TO CAMPAIGN 3, NOT PRECEDENT. It reaches the legacy transport
 * (`@api/API`, `@api/endpoints`) and response type (`@api/types`), which `shared` may not do once
 * the campaign lands; the reasoning is recorded in docs/FRONTEND_MIGRATION.md (campaign 3).
 */
const getSuperadminCompany = async (
  type: string,
  id: string | number,
): Promise<GetSuperadminCompanyResponse> => {
  const response = await API().get<GetSuperadminCompanyResponse>(
    ENDPOINTS.superadmin.companyItem(type, id),
  );
  return response.data;
};

export const useGetSuperadminCompany = (type: string, id: string | number, enabled = true) =>
  useQuery({
    queryKey: adminKeys.company(type, id),
    queryFn: () => getSuperadminCompany(type, id),
    enabled: Boolean(type && id && enabled),
  });
